import { Link } from 'react-router-dom';

export default function AboutPage() {
  return (
    <main>
      {/* Hero — Full Background */}
      <section className="relative h-[70vh] md:h-[80vh] min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20London%20skyline%20at%20golden%20hour%2C%20the%20City%20of%20London%20financial%20district%20with%20modern%20glass%20skyscrapers%2C%20the%20Shard%20and%20Gherkin%20visible%2C%20warm%20amber%20sunlight%20reflecting%20on%20the%20Thames%20river%2C%20dramatic%20sky%20with%20golden%20clouds%2C%20premium%20British%20financial%20photography%2C%20vibrant%20professional%20atmosphere%2C%20bright%20inspiring%20scene&width=1920&height=900&seq=about-hero-london-2026&orientation=landscape"
            alt="Creighton Investments Ltd — London, United Kingdom"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-background-50" />
        </div>
        <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[10px] uppercase tracking-[3px] text-primary-300 font-semibold">
              Creighton Investments Ltd
            </span>
            <h1 className="text-5xl md:text-7xl font-heading font-bold text-white mt-4 mb-6 tracking-tight">
              About Us
            </h1>
            <p className="text-lg text-white/80 leading-relaxed max-w-2xl">
              A UK-based premium investment firm deploying institutional capital across five diversified sectors. Founded in 2016 and headquartered at 12 Gateway Mews in Bounds Green, London, we combine British corporate governance with global opportunity.
            </p>
          </div>
        </div>
      </section>

      {/* Swiss Precision. Global Vision. */}
      <section className="relative pt-16 pb-16 md:pt-24 md:pb-24 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold">
                About the Firm
              </span>
              <h2 className="text-4xl md:text-6xl font-heading font-bold text-foreground-950 mt-4 mb-6 tracking-tight">
                British Precision.<br />Global Vision.
              </h2>
              <p className="text-lg text-foreground-500 leading-relaxed">
                Creighton Investments Ltd is a UK-based premium investment firm deploying institutional capital across five diversified sectors. Founded in 2016 and headquartered in London, we combine British corporate governance with global opportunity.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20modern%20London%20corporate%20office%20interior%20with%20warm%20wood%20and%20natural%20light%2C%20view%20of%20green%20London%20mews%20through%20large%20windows%2C%20professional%20meeting%20room%2C%20clean%20minimalist%20British%20design%2C%20premium%20atmosphere%2C%20bright%20and%20airy&width=700&height=500&seq=about-hero-office-london-2026&orientation=landscape"
                alt="Creighton Investments Ltd Office — London"
                className="w-full h-auto rounded-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story — The Journey */}
      <section className="relative py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold">
              Our Story
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mt-4 mb-8 tracking-tight">
              From a London Mews to Global Markets
            </h2>

            <div className="prose-custom space-y-6 text-foreground-600 text-base leading-relaxed">
              <p>
                It began with a conviction. On the 29th of June 2016, <strong className="text-foreground-900">Stavros Constantinides</strong> incorporated Creighton Investments Ltd as a private limited company at 12 Gateway Mews in Bounds Green, North London. While global capital chased short-term speculation, he recognized that the most durable wealth is built through disciplined, diversified, long-term investment — anchored in the values that define British corporate governance: transparency, accountability, and prudence.
              </p>
              <p>
                The choice of Bounds Green was deliberate. Away from the frenzy of Canary Wharf and the City, the quiet residential mews offered something invaluable: clarity of thought. The private limited company structure meant rigorous governance from day one — Companies House registration, statutory filings, and a governance framework built for institutional partners.
              </p>
              <p>
                The early years were methodical. Constantinides spent them building the scaffolding of what would become a multi-sector investment platform — cultivating relationships with institutional partners across Europe, developing proprietary due diligence frameworks, and assembling a network of sector specialists who shared his philosophy: invest where you have deep conviction, not where consensus points.
              </p>
              <p>
                By 2018, the firm made its first landmark real estate acquisition — a commercial property in Central London. That investment, still in the portfolio today, set the template: acquire premium assets in gateway cities, enhance value through active management, and hold for the long term. Over the following years, the portfolio expanded methodically across sectors — renewable energy, oil and gas infrastructure, digital assets and blockchain, and medical equipment.
              </p>
              <p>
                Today, the firm manages <strong className="text-foreground-900">€2.4 billion in assets across 47 active investments in 18 countries</strong>. But the founding philosophy remains unchanged. Every investment decision still passes through the same rigorous governance framework that Constantinides established on day one. The registered office remains at 12 Gateway Mews. And the conviction that disciplined capital builds the future — that conviction has only deepened with time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Directors — Experience & Impact */}
      <section className="relative py-20 md:py-28 bg-background-50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold">
              Leadership
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mt-4 mb-4 tracking-tight">
              The Architects of Our Success
            </h2>
            <p className="text-foreground-500 text-base leading-relaxed mb-12 max-w-2xl">
              Our directors bring decades of cross-sector expertise in finance, governance, and international investment. Their combined experience spans institutional asset management, cross-border structuring, regulatory compliance, and strategic portfolio allocation — the foundation upon which every investment decision rests.
            </p>

            {/* Director 1 — Stavros Constantinides */}
            <div className="bg-white rounded-2xl border border-foreground-100/20 p-8 md:p-10 mb-8">
              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="shrink-0">
                  <img
                    src="https://storage.readdy-site.link/project_files/cd9486cf-2809-47e9-86b7-caa7ed7f1efa/9ff16c8f-8952-4373-a06b-6da20e398ffe_compressed_leo.webp"
                    alt="Stavros Constantinides"
                    className="w-28 h-28 md:w-36 md:h-36 rounded-2xl object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold text-foreground-950">Stavros Constantinides</h3>
                  <p className="text-primary-500 text-xs uppercase tracking-[2px] font-semibold mt-1 mb-4">
                    Director &amp; Founder
                  </p>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-3">
                    Stavros Constantinides is the founder and director of Creighton Investments Ltd, having incorporated the company on 29 June 2016. A Cypriot national with decades of international business experience, he brings strategic vision and disciplined governance to every aspect of the firm's operations.
                  </p>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-3">
                    As Director, Constantinides sets the firm's long-term vision and chairs all major investment decisions. His discipline in risk assessment and his commitment to UK corporate governance standards have been the defining forces behind the firm's consistent performance. Under his stewardship, the portfolio has grown from a single London property to €2.4 billion in diversified assets.
                  </p>
                  <p className="text-foreground-500 text-sm leading-relaxed">
                    Constantinides' network across European institutional investors, sovereign wealth funds, and family offices has been instrumental in securing the strategic partnerships that underpin the firm's growth. His leadership philosophy combines rigorous analysis with long-term conviction — the same principles that have guided the firm since its founding.
                  </p>
                  <a href="mailto:stavros.constantinides@creightoninvest.com" className="inline-flex items-center gap-1.5 text-primary-500 hover:text-primary-600 text-xs mt-4 transition-colors cursor-pointer">
                    <i className="ri-mail-line" /> stavros.constantinides@creightoninvest.com
                  </a>
                </div>
              </div>
            </div>

            {/* Director 2 — Eleni Constantinidi */}
            <div className="bg-white rounded-2xl border border-foreground-100/20 p-8 md:p-10">
              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="shrink-0">
                  <img
                    src="https://public.readdy.ai/ai/img_res/edited_0f8af98c21e0c42db103efadbdd3d25b_2f2cbed3.jpg"
                    alt="Eleni Constantinidi"
                    className="w-28 h-28 md:w-36 md:h-36 rounded-2xl object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold text-foreground-950">Eleni Constantinidi</h3>
                  <p className="text-primary-500 text-xs uppercase tracking-[2px] font-semibold mt-1 mb-4">
                    Director
                  </p>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-3">
                    Appointed to the board of Creighton Investments Ltd on 25 March 2026, Eleni Constantinidi brings fresh perspective and operational acumen to the firm's leadership. A Cypriot national, she plays a key role in portfolio monitoring, compliance oversight, and strategic planning.
                  </p>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-3">
                    Constantinidi personally oversees all legal and compliance operations, from cross-border investment structuring to regulatory filings with Companies House. Her meticulous approach ensures the firm's flawless compliance record across multiple jurisdictions and its unwavering commitment to UK corporate governance standards.
                  </p>
                  <p className="text-foreground-500 text-sm leading-relaxed">
                    Constantinidi's role extends beyond compliance — she is a key member of the investment committee, where her analytical capabilities and risk assessment skills provide essential balance to the portfolio construction process. Her presence ensures that the firm's pursuit of returns never comes at the expense of the governance standards that define British investment excellence.
                  </p>
                  <a href="mailto:eleni.constantinidi@creightoninvest.com" className="inline-flex items-center gap-1.5 text-primary-500 hover:text-primary-600 text-xs mt-4 transition-colors cursor-pointer">
                    <i className="ri-mail-line" /> eleni.constantinidi@creightoninvest.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission + Values */}
      <section className="relative py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold">
                Our Mission
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mt-4 mb-6 tracking-tight">
                Deploying Capital with Discipline and Purpose
              </h2>
              <p className="text-foreground-500 text-base leading-relaxed mb-4">
                We identify, underwrite, and manage investments that generate superior risk-adjusted returns while contributing to economic development and technological progress. Our multi-sector approach provides natural diversification across geographies, asset types, and market cycles.
              </p>
              <p className="text-foreground-500 text-base leading-relaxed mb-6">
                Every investment decision is evaluated through our proprietary due diligence framework — combining quantitative analysis, on-the-ground research, and rigorous British governance standards. We invest where we have conviction, not where consensus leads.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Discipline', desc: 'Research-driven underwriting' },
                  { label: 'Diversification', desc: 'Five uncorrelated sectors' },
                  { label: 'Transparency', desc: 'UK corporate governance' },
                  { label: 'Longevity', desc: '10+ years track record' },
                ].map((v) => (
                  <div key={v.label} className="bg-background-50 rounded-xl px-5 py-4 border border-foreground-100/20">
                    <p className="text-sm font-heading font-bold text-foreground-950">{v.label}</p>
                    <p className="text-xs text-foreground-400 mt-0.5">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Wide%20panoramic%20view%20of%20the%20River%20Thames%20and%20London%20skyline%20on%20a%20bright%20summer%20day%2C%20Tower%20Bridge%20and%20modern%20skyscrapers%2C%20professional%20landscape%20photography%2C%20vibrant%20natural%20colors%2C%20serene%20premium%20atmosphere&width=700&height=560&seq=about-london-thames-003&orientation=landscape"
                alt="River Thames — London, United Kingdom"
                className="w-full h-auto rounded-2xl object-cover"
              />
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl px-5 py-4 border border-foreground-100/20 hidden lg:block">
                <p className="text-2xl font-heading font-bold text-foreground-950">2016</p>
                <p className="text-[10px] uppercase tracking-[2px] text-foreground-400">Founded</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-20 md:py-28 bg-background-50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold">
              Our Journey
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mt-4 tracking-tight">
              Key Milestones
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {[
              { year: '2016', title: 'Founded in London', desc: 'Stavros Constantinides incorporates Creighton Investments Ltd as a private limited company at 12 Gateway Mews, Bounds Green, London — laying the foundation for disciplined, diversified investment under UK corporate governance.' },
              { year: '2018', title: 'First Real Estate Acquisition', desc: 'Entry into UK commercial real estate with a prime Central London office property — the template for all future acquisitions: premium assets, active management, long-term hold.' },
              { year: '2020', title: 'Energy & Digital Assets Entry', desc: 'First renewable energy investments and strategic allocation to blockchain infrastructure. The firm becomes one of the first UK institutions to provide regulated crypto exposure to investors.' },
              { year: '2021', title: '€1B AUM Milestone', desc: 'Portfolio diversification into oil & gas infrastructure assets. Assets under management surpass €1 billion for the first time.' },
              { year: '2023', title: 'MedTech Expansion', desc: 'Entry into medical equipment with acquisitions in surgical robotics and AI-powered diagnostic imaging, expanding the portfolio to five sectors.' },
              { year: '2024', title: '€2B+ AUM Milestone', desc: 'Assets under management surpass €2 billion, driven by strong performance across all five investment sectors.' },
              { year: '2026', title: 'Global Reach', desc: 'Eleni Constantinidi appointed to the board. 47 active investments across 18 countries, €2.4B+ AUM, 12.8% average annual return. The founding vision — now a global investment platform.' },
            ].map((m, i) => (
              <div key={m.year} className="relative flex gap-8 pb-10 last:pb-0">
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full bg-primary-500 shrink-0 mt-1.5" />
                  {i < 6 && <div className="w-px flex-1 bg-foreground-100/40 mt-2" />}
                </div>
                <div>
                  <span className="text-xs uppercase tracking-[2px] text-primary-500 font-bold">{m.year}</span>
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mt-1">{m.title}</h3>
                  <p className="text-foreground-500 text-sm mt-1.5">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Governance */}
      <section className="relative py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold">
              Governance
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mt-4 mb-6 tracking-tight">
              British Institutional Standards
            </h2>
            <p className="text-foreground-500 text-base leading-relaxed mb-8">
              As a UK private limited company registered with Companies House, we adhere to the highest standards of corporate governance. Our board of directors provides strategic oversight, while our investment committee ensures rigorous underwriting and portfolio monitoring.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
              {[
                { title: 'Board of Directors', items: ['Stavros Constantinides — Director', 'Eleni Constantinidi — Director'] },
                { title: 'Registration', items: ['Company No. 10256484', 'Incorporated 29 June 2016', 'Registered in England & Wales'] },
                { title: 'Registered Office', items: ['12 Gateway Mews', 'Bounds Green', 'London, N11 2UT'] },
              ].map((col) => (
                <div key={col.title} className="bg-background-50 rounded-xl p-6 border border-foreground-100/20">
                  <h4 className="text-sm font-heading font-bold text-foreground-950 mb-3">{col.title}</h4>
                  <ul className="flex flex-col gap-2">
                    {col.items.map((item) => (
                      <li key={item} className="text-foreground-500 text-sm">{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-20 bg-background-50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 tracking-tight">
            Partner With a Proven Team
          </h2>
          <p className="text-foreground-500 text-base max-w-lg mx-auto mb-8">
            Our directors and investment team are available to discuss your objectives and explore how our institutional platform can serve your portfolio.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm uppercase tracking-[2px] font-semibold px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Get in Touch
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </main>
  );
}